/**
 * useAmbientRecording hook - Continuous ambient audio recording with chunked transcription.
 *
 * Records audio continuously using MediaRecorder with automatic 30-second chunking.
 * Each chunk is sent to the backend for transcription, building a progressive transcript.
 *
 * MIME type order differs from useAudioRecorder.ts — see getSupportedMimeType for rationale.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { AmbientChunkResponse, AmbientStopResponse, AmbientExtractionResponse } from '@/services/api'

/** Chunk duration in milliseconds */
const CHUNK_DURATION_MS = 30000

/**
 * Transcript chunk from the backend
 */
export interface TranscriptChunk {
  chunkIndex: number
  text: string
  cumulativeWordCount: number
  timestamp: Date
}

/**
 * Ambient recording state
 */
export interface AmbientRecordingState {
  isRecording: boolean
  isExtracting: boolean
  isSupported: boolean
  error: string | null
  chunks: TranscriptChunk[]
  totalDuration: number
  cumulativeWordCount: number
  extraction: AmbientExtractionResponse | null
}

/**
 * Ambient recording actions
 */
export interface AmbientRecordingActions {
  startRecording: () => Promise<void>
  stopRecording: () => Promise<AmbientStopResponse | null>
  extractThemes: () => Promise<AmbientExtractionResponse | null>
  reset: () => void
}

export type UseAmbientRecordingReturn = AmbientRecordingState & AmbientRecordingActions

interface UseAmbientRecordingOptions {
  sessionId: string
  sendChunk: (sessionId: string, audioData: Blob, chunkIndex: number) => Promise<AmbientChunkResponse>
  stopAmbient: (sessionId: string) => Promise<AmbientStopResponse>
  extractAmbient: (sessionId: string) => Promise<AmbientExtractionResponse>
}

/**
 * Check if MediaRecorder is supported
 */
function isMediaRecorderSupported(): boolean {
  return typeof window !== 'undefined' && 'MediaRecorder' in window
}

/**
 * Get the best supported audio MIME type.
 *
 * Order matters: Azure Fast Transcription accepts webm/opus and mp4/AAC, but
 * rejects mp4 with an Opus codec (Chrome 115+ can produce that combo if we
 * ask for bare `audio/mp4` first). Prefer webm on Chromium/Firefox; pin
 * AAC-LC explicitly when falling through to mp4 (Safari) so a future
 * Chromium with webm disabled cannot silently revert to mp4/Opus.
 */
function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/wav',
  ]

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }

  return ''
}

/**
 * Classify a transcription error as sticky (4xx — auth/format/quota) vs
 * transient (network blip, server flap). Sticky errors latch until reset.
 *
 * Prefer axios `response.status` when present so we don't misread a literal
 * 4xx in the error message body. Fall back to a status-prefixed idiom match
 * — bare `\b4\d\d\b` matches "returned 412 bytes" (space = word boundary) but
 * NOT "400ms" (the `m` is a word character, so no boundary forms after the
 * digit). The status-prefixed patterns add a redundant-but-explicit guard.
 * Keyword fallback is anchored to wording the backend actually emits.
 */
function isStickyError(err: unknown): boolean {
  const status = (err as { response?: { status?: number }; status?: number })?.response?.status
    ?? (err as { status?: number })?.status
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return true
  }
  if (!(err instanceof Error)) return false
  const message = err.message
  if (/\bstatus[:\s]+4\d\d\b/i.test(message)) return true
  if (/\bHTTP\s+4\d\d\b/i.test(message)) return true
  if (/\b(unauthori[sz]ed|forbidden|unsupported\s+format|invalid\s+audio)\b/i.test(message)) return true
  return false
}


export function useAmbientRecording({
  sessionId,
  sendChunk,
  stopAmbient,
  extractAmbient,
}: UseAmbientRecordingOptions): UseAmbientRecordingReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chunks, setChunks] = useState<TranscriptChunk[]>([])
  const [totalDuration, setTotalDuration] = useState(0)
  const [cumulativeWordCount, setCumulativeWordCount] = useState(0)
  const [extraction, setExtraction] = useState<AmbientExtractionResponse | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const chunkIndexRef = useRef(0)
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Controls the stop/restart chunk cycle — set false to prevent next cycle starting
  const chunkCycleActiveRef = useRef(false)
  const chunkCycleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const pendingChunksRef = useRef<Set<Promise<void>>>(new Set())
  const consecutiveTinyChunksRef = useRef(0)
  const stickyErrorRef = useRef<string | null>(null)
  const consecutiveSuccessRef = useRef(0)
  // Timer for auto-clearing transient (non-sticky) chunk-failure banners. Without
  // this, a single 5xx mid-recording leaves an alarming red error sitting on the
  // panel for the full 30s gap until the next chunk uploads — even though
  // recording is healthy and continues capturing.
  const transientErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSupported = isMediaRecorderSupported()

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current)
      if (chunkCycleTimerRef.current) clearTimeout(chunkCycleTimerRef.current)
      if (transientErrorTimeoutRef.current) clearTimeout(transientErrorTimeoutRef.current)
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch((err: unknown) => {
          console.warn('Wake lock release failed on unmount:', err instanceof Error ? err.message : String(err))
        })
      }
    }
  }, [])

  /**
   * Request Wake Lock to keep screen on during recording
   */
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
      }
    } catch (err) {
      // Non-fatal — but log so kiosk deployments can diagnose screen-off issues
      console.warn('Wake lock unavailable:', err instanceof Error ? err.name : String(err))
    }
  }, [])

  /**
   * Release Wake Lock
   */
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release()
      } catch (err: unknown) {
        console.warn('Wake lock release failed:', err instanceof Error ? err.message : String(err))
      }
      wakeLockRef.current = null
    }
  }, [])

  /**
   * Handle a chunk of audio data
   */
  const handleChunkData = useCallback(async (data: Blob) => {
    // Discard chunks below 1 KiB — empirically below this size Azure Fast
    // Transcription rejects the upload regardless of multipart envelope overhead.
    // After 3+ consecutive tiny chunks, surface in-UI so the participant knows
    // something is wrong — but never overwrite an active sticky 4xx banner,
    // which indicates a deeper auth/format problem rather than a mic issue.
    if (data.size < 1024) {
      consecutiveTinyChunksRef.current += 1
      console.warn(`Ambient chunk skipped: ${data.size} bytes below 1KB minimum`)
      if (consecutiveTinyChunksRef.current >= 3 && !stickyErrorRef.current) {
        setError('Audio chunks too small to transcribe — check microphone setup')
      }
      return
    }
    consecutiveTinyChunksRef.current = 0

    const currentIndex = chunkIndexRef.current
    chunkIndexRef.current += 1

    try {
      const result = await sendChunk(sessionId, data, currentIndex)
      const newChunk: TranscriptChunk = {
        chunkIndex: result.chunk_index,
        text: result.text,
        cumulativeWordCount: result.cumulative_word_count,
        timestamp: new Date(),
      }
      setChunks(prev => [...prev, newChunk])
      setCumulativeWordCount(result.cumulative_word_count)
      consecutiveSuccessRef.current += 1
      // Sticky 4xx persists unless backend has been healthy for 5+ consecutive
      // chunks — that many sustained 200s means the earlier auth/format failure
      // was genuinely transient and the banner should clear. Below that threshold
      // we keep showing the sticky error so intermittent failures stay visible.
      if (stickyErrorRef.current && consecutiveSuccessRef.current >= 5) {
        stickyErrorRef.current = null
        setError(null)
      } else if (!stickyErrorRef.current) {
        setError(null)
      }
      if (transientErrorTimeoutRef.current) {
        clearTimeout(transientErrorTimeoutRef.current)
        transientErrorTimeoutRef.current = null
      }
    } catch (err) {
      consecutiveSuccessRef.current = 0
      const message = err instanceof Error ? err.message : 'Failed to transcribe chunk'
      console.error(`Ambient chunk ${currentIndex} failed:`, message)
      // Sticky 4xx wins over later transient errors — a systematic auth/format
      // failure must not be masked by a subsequent network blip. Other errors
      // use latest-wins so live state reflects current health.
      const isSticky = isStickyError(err)
      if (isSticky && !stickyErrorRef.current) {
        stickyErrorRef.current = `Transcription failed: ${message}`
        setError(stickyErrorRef.current)
      } else if (!stickyErrorRef.current) {
        // Soften wording + auto-clear after 10s (see transientErrorTimeoutRef).
        setError(`A chunk couldn't be transcribed — recording continues. (${message})`)
        if (transientErrorTimeoutRef.current) clearTimeout(transientErrorTimeoutRef.current)
        transientErrorTimeoutRef.current = setTimeout(() => {
          if (!stickyErrorRef.current) setError(null)
          transientErrorTimeoutRef.current = null
        }, 10000)
      }
    }
  }, [sessionId, sendChunk])

  /**
   * Start continuous ambient recording
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser')
      return
    }

    setError(null)
    setChunks([])
    setCumulativeWordCount(0)
    setExtraction(null)
    chunkIndexRef.current = 0
    consecutiveTinyChunksRef.current = 0
    consecutiveSuccessRef.current = 0
    stickyErrorRef.current = null

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      })

      mediaStreamRef.current = stream

      // Stop/restart cycle — each recorder instance produces one complete, self-contained
      // WebM file. MediaRecorder.start(timeslice) only embeds the EBML/Segment init
      // header in chunk 0; subsequent chunks are bare Cluster fragments that Azure Fast
      // Transcription rejects as invalid format. Stopping and restarting gives every chunk
      // its own init header. Audio captured during the stop→onstop→start transition
      // (~50–200ms) is lost; at 30s intervals this is under 1% of content and acceptable
      // for coarse theme extraction.
      const startChunkCycle = () => {
        if (!chunkCycleActiveRef.current) return
        if (!mediaStreamRef.current) {
          // Stream disappeared mid-cycle (e.g. OS revoked mic) — surface error, halt.
          chunkCycleActiveRef.current = false
          if (chunkCycleTimerRef.current) { clearTimeout(chunkCycleTimerRef.current); chunkCycleTimerRef.current = null }
          if (durationIntervalRef.current) { clearInterval(durationIntervalRef.current); durationIntervalRef.current = null }
          void releaseWakeLock()
          setIsRecording(false)
          const streamLostMsg = 'Recording stopped unexpectedly — microphone stream was lost. Please restart.'
          // Set sticky ref to prevent in-flight handleChunkData successes from clearing this banner.
          pendingChunksRef.current.clear()
          stickyErrorRef.current = streamLostMsg
          setError(streamLostMsg)
          return
        }

        const cycleMimeType = getSupportedMimeType()
        const cycleRecorder = new MediaRecorder(
          mediaStreamRef.current,
          cycleMimeType ? { mimeType: cycleMimeType } : {}
        )
        mediaRecorderRef.current = cycleRecorder

        cycleRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            const promise = handleChunkData(event.data)
            pendingChunksRef.current.add(promise)
            void promise.finally(() => pendingChunksRef.current.delete(promise))
          }
        }

        cycleRecorder.onerror = (event: Event) => {
          const err = (event as { error?: DOMException }).error
          const errName = err?.name ?? 'UnknownError'
          console.error('MediaRecorder error:', errName, err?.message ?? '')
          chunkCycleActiveRef.current = false
          // Clear Set so stopRecording's Promise.allSettled sees nothing to await.
          // stickyErrorRef is set to the error message (not null) so that any
          // in-flight handleChunkData promises that resolve after this handler
          // skip their setError(null) call — otherwise they would clear the banner.
          pendingChunksRef.current.clear()
          if (chunkCycleTimerRef.current) {
            clearTimeout(chunkCycleTimerRef.current)
            chunkCycleTimerRef.current = null
          }
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current)
            durationIntervalRef.current = null
          }
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop())
            mediaStreamRef.current = null
          }
          void releaseWakeLock()
          mediaRecorderRef.current = null
          setIsRecording(false)
          const onerrorMsg = `Recording stopped unexpectedly (${errName}). Please restart the recording.`
          setError(onerrorMsg)
          stickyErrorRef.current = onerrorMsg
          consecutiveSuccessRef.current = 0
          consecutiveTinyChunksRef.current = 0
        }

        cycleRecorder.onstop = () => {
          // Restart immediately if still recording — stream tracks stay open across cycles
          if (chunkCycleActiveRef.current) {
            startChunkCycle()
          } else {
            // User stopped: clean up stream tracks
            if (mediaStreamRef.current) {
              mediaStreamRef.current.getTracks().forEach(track => track.stop())
              mediaStreamRef.current = null
            }
          }
        }

        try {
          cycleRecorder.start()
        } catch (err: unknown) {
          // start() throws synchronously for InvalidStateError / SecurityError — onerror
          // does not fire for synchronous throws, so we must handle cleanup here.
          const errName = err instanceof Error ? err.name : 'UnknownError'
          const errMsg = err instanceof Error ? err.message : String(err)
          console.error('MediaRecorder.start() threw:', errName, errMsg)
          chunkCycleActiveRef.current = false
          pendingChunksRef.current.clear()
          if (chunkCycleTimerRef.current) { clearTimeout(chunkCycleTimerRef.current); chunkCycleTimerRef.current = null }
          if (durationIntervalRef.current) { clearInterval(durationIntervalRef.current); durationIntervalRef.current = null }
          if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null }
          void releaseWakeLock()
          mediaRecorderRef.current = null
          setIsRecording(false)
          const startThrowMsg = `Recording stopped unexpectedly (${errName}). Please restart the recording.`
          stickyErrorRef.current = startThrowMsg
          setError(startThrowMsg)
          return
        }

        chunkCycleTimerRef.current = setTimeout(() => {
          chunkCycleTimerRef.current = null
          if (cycleRecorder.state === 'recording') {
            cycleRecorder.stop()
          }
        }, CHUNK_DURATION_MS)
      }

      chunkCycleActiveRef.current = true
      startChunkCycle()

      setIsRecording(true)
      startTimeRef.current = Date.now()

      durationIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setTotalDuration(Math.floor((Date.now() - startTimeRef.current) / 1000))
        }
      }, 1000)

      await requestWakeLock()

    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Microphone access denied. Please allow microphone access in your browser settings.')
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone.')
        } else {
          setError(`Microphone error: ${err.message}`)
        }
      } else if (err instanceof TypeError) {
        setError(`Recording setup failed (codec issue): ${err.message}. Try refreshing the page.`)
      } else {
        setError(`Failed to start recording: ${err instanceof Error ? err.message : String(err)}`)
      }
    }
    // requestWakeLock and releaseWakeLock are both useCallback with [] deps, so
    // their identity is stable across renders — listing them here is correct per
    // exhaustive-deps and does not cause re-creation churn. Other accesses inside
    // this callback go through refs (mediaRecorderRef, mediaStreamRef,
    // durationIntervalRef, chunkCycleActiveRef, chunkCycleTimerRef, wakeLockRef,
    // stickyErrorRef, pendingChunksRef, chunkIndexRef, startTimeRef,
    // consecutiveSuccessRef, consecutiveTinyChunksRef) which React's deps rule
    // explicitly does not require.
  }, [isSupported, handleChunkData, requestWakeLock, releaseWakeLock])

  /**
   * Stop recording and notify the backend
   */
  const stopRecording = useCallback(async (): Promise<AmbientStopResponse | null> => {
    // Signal cycle to not restart, then clear pending chunk timer
    chunkCycleActiveRef.current = false
    if (chunkCycleTimerRef.current) {
      clearTimeout(chunkCycleTimerRef.current)
      chunkCycleTimerRef.current = null
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }

    // Stop the recorder and await the final ondataavailable + onstop events.
    // Safety ceiling of 5s via Promise.race: if the recorder never fires onstop
    // (e.g. permission revocation mid-stop, or a browser that stalls on stop())
    // the timeout branch wins, forcing stream cleanup so the mic indicator clears.
    // Shared cleanupStream() so tracks are stopped regardless of which branch wins.
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      let streamCleanedUp = false
      const cleanupStream = () => {
        if (!streamCleanedUp && mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop())
          mediaStreamRef.current = null
          streamCleanedUp = true
        }
      }

      const onstopFired = await Promise.race([
        new Promise<boolean>((resolve) => {
          recorder.onstop = () => { cleanupStream(); resolve(true) }
          recorder.stop()
        }),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 5000)),
      ])

      if (!onstopFired) {
        console.error('MediaRecorder.stop() timed out after 5s — forcing stream cleanup')
        cleanupStream()
      }
    }
    mediaRecorderRef.current = null

    // Await any in-flight chunk transcriptions before notifying the backend
    if (pendingChunksRef.current.size > 0) {
      await Promise.allSettled([...pendingChunksRef.current])
    }

    setIsRecording(false)
    await releaseWakeLock()

    try {
      const result = await stopAmbient(sessionId)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to stop recording'
      console.error('stopAmbient failed:', message)
      setError('Could not notify the server that recording stopped. Your audio has been saved.')
      return null
    }
  }, [sessionId, stopAmbient, releaseWakeLock])

  /**
   * Extract themes from the accumulated transcript
   */
  const extractThemes = useCallback(async (): Promise<AmbientExtractionResponse | null> => {
    setIsExtracting(true)
    // Don't clear a sticky 4xx banner before the request resolves — the sticky
    // ref and React state would diverge until the catch fires its own setError.
    if (!stickyErrorRef.current) {
      setError(null)
    }

    try {
      const result = await extractAmbient(sessionId)
      setExtraction(result)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to extract themes'
      if (stickyErrorRef.current) {
        // Sticky 4xx from transcription is still the root cause — keep it visible
        // but append the extract failure so the participant sees both. Previous
        // behaviour swallowed extract here, so the user saw a stale transcription
        // error and never learned theme extraction had also failed.
        console.warn('extractThemes failed (sticky error active):', message)
        setError((prev) => prev ? `${prev}. Theme extraction also failed.` : message)
      } else {
        console.error('extractThemes failed:', message)
        setError('Theme extraction failed — your audio was recorded. Please try stopping and restarting.')
      }
      return null
    } finally {
      setIsExtracting(false)
    }
  }, [sessionId, extractAmbient])

  /**
   * Reset all state (stops recording if active)
   */
  const reset = useCallback(() => {
    // Disarm the cycle flag and timer BEFORE stopping the recorder — onstop can fire
    // within the same microtask turn in some browsers and would otherwise see
    // chunkCycleActiveRef=true and call startChunkCycle(), restarting recording during reset.
    chunkCycleActiveRef.current = false
    if (chunkCycleTimerRef.current) {
      clearTimeout(chunkCycleTimerRef.current)
      chunkCycleTimerRef.current = null
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    mediaRecorderRef.current = null
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch((err: unknown) => {
        console.warn('Wake lock release failed:', err instanceof Error ? err.message : String(err))
      })
      wakeLockRef.current = null
    }

    setIsRecording(false)
    setChunks([])
    setCumulativeWordCount(0)
    setTotalDuration(0)
    setError(null)
    setExtraction(null)
    chunkIndexRef.current = 0
    consecutiveTinyChunksRef.current = 0
    consecutiveSuccessRef.current = 0
    stickyErrorRef.current = null
  }, [])

  return {
    isRecording,
    isExtracting,
    isSupported,
    error,
    chunks,
    totalDuration,
    cumulativeWordCount,
    extraction,
    startRecording,
    stopRecording,
    extractThemes,
    reset,
  }
}
